import React, { FC, useState, useEffect } from 'react';
import styles from '../AnnotationForm.module.scss';
import { AssessmentAssigneeOption, scrollToBottomOfAnnotationModal } from '../Helpers';
import { sendFindingForAssessment, getAnnotationAssessment, canCloseAssessment } from '../AnnotationForm.slice';
import { AnnotationAssessment } from '../AnnotationForm.types';
import { RootState } from '../../../../../rootReducer';
import { AppDispatch } from '../../../../../store';
import { connect } from 'react-redux';
import Busy from '../../../../Shared/Busy';
import { fadein } from '../../../../Shared/Motion';
import { motion } from 'framer-motion';
import AssessmentInput from './AssessmentInput';
import AssessmentAssignee from './AssessmentAssignee';

export type StoreProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

export type Props = {
  assessment: AnnotationAssessment | null;
  scrollToBottom: boolean;
  assessmentCompleted: boolean;
  canSaveAssessment: boolean;
  description: string;
  annotationId: string | null;
  setDescription: (value: string) => void;
} & StoreProps;

export const Assessment: FC<Props> = ({
  assessment,
  scrollToBottom,
  canSaveAssessment,
  description,
  annotationId,
  isWarrantyCaseClosed,
  loadingAssessment,
  isSgreUser,
  setDescription,
  onSetForAssessment,
  startLoadingAssessment,
  closeAssessment
}) => {
  const [assignee, setAssignee] = useState(AssessmentAssigneeOption.Own);

  useEffect(() => {
    if (scrollToBottom && !loadingAssessment) {
      scrollToBottomOfAnnotationModal();
    }
  }, [loadingAssessment, scrollToBottom]);

  useEffect(() => {
    if (annotationId) {
      startLoadingAssessment();
    }
  }, [annotationId, startLoadingAssessment]);

  useEffect(() => {
    if (assessment && !loadingAssessment) {
      setDescription(assessment.description);
    }
  }, [assessment, loadingAssessment, setDescription]);

  useEffect(() => {
    if (!canSaveAssessment) {
      return;
    }

    if (!assessment && description.length) {
      onSetForAssessment(description, assignee);
      return;
    }

    closeAssessment();
  }, [assessment, assignee, canSaveAssessment, closeAssessment, description, onSetForAssessment]);

  if (loadingAssessment) {
    return (
      <>
        <hr className={styles.sectorSeparator} />
        <div className={styles.busyContainer}>
          <Busy />
        </div>
      </>
    );
  }

  return (
    <motion.div initial='hidden' animate='visible' variants={fadein} className={styles.sendForAssessmentContainer}>
      <hr className={styles.sectorSeparator} />
      <h5>Assessment</h5>
      {!assessment && <AssessmentAssignee disabled={false} onChange={setAssignee} value={assignee} isSgreUser={isSgreUser} />}
      <AssessmentInput
        label='Description'
        disabled={!!assessment || isWarrantyCaseClosed}
        placeholder='Enter Description'
        value={description}
        required={false}
        onChange={setDescription}
        rows={6}
      />

      <div id='assessment-bottom'></div>
    </motion.div>
  );
};

const mapStateToProps = (state: RootState) => ({
  loadingAssessment: state.annotationForm.loadingAssessment,
  isSgreUser: state.annotationForm.isSgreUser,
  isWarrantyCaseClosed: state.annotationForm.isWarrantyCaseClosed
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onSetForAssessment: (description: string, assignee: AssessmentAssigneeOption) => dispatch(sendFindingForAssessment(description, assignee)),
  startLoadingAssessment: () => dispatch(getAnnotationAssessment()),
  closeAssessment: () => dispatch(canCloseAssessment())
});

export default connect(mapStateToProps, mapDispatchToProps)(Assessment);
